import type {
    IndicatorPainter,
    IndicatorPainterContext,
    IndicatorPainterPatchContext,
    IndicatorPaintResult,
} from './indicator-painter.js';
import { applyMappedRuntimePatch, valuePoint } from './runtime-patch.js';

/** Plain-line painter used both as the fallback and by the catalog's explicit `line` kind. */
export class DefaultIndicatorPainter implements IndicatorPainter {
    paint(context: IndicatorPainterContext): IndicatorPaintResult {
        // `context.entry` is the shell-owned catalog entry (typed `any` on the painter contract),
        // so the output-name list has to be pinned here or `.map` callbacks below infer implicit any.
        const outputs: string[] = context.entry.outputNames?.length ? context.entry.outputNames : ['value'];
        const colors: string[] = [];
        const series: any[] = [];

        for (let i = 0; i < outputs.length; i++) {
            const color = context.nextColor();
            colors.push(color);
            const title = outputs.length === 1
                ? (context.settings?.name || context.entry.type)
                : outputs[i];
            const item = context.addSeries('line', {
                color,
                lineWidth: outputs.length === 1 ? 2 : 1,
                title,
            }, context.output(outputs[i]));
            series.push(item);
        }

        if (context.settings?.levels && series[0]?.createPriceLine) {
            for (const level of context.settings.levels) {
                series[0].createPriceLine({
                    price: level,
                    color: 'rgba(107,122,141,0.4)',
                    lineWidth: 1,
                    lineStyle: 2,
                    axisLabelVisible: false,
                });
            }
        }

        return {
            series,
            styleSources: Object.fromEntries(outputs.map((key, index) => [key, index])),
            colors,
            legendSources: Object.fromEntries(outputs.map((key, index) => [
                key,
                { seriesIndex: index, field: 'value' },
            ])),
        };
    }

    update(context: IndicatorPainterContext, series: any[]): void {
        const outputs: string[] = context.entry.outputNames?.length ? context.entry.outputNames : ['value'];
        for (let i = 0; i < outputs.length && i < series.length; i++) {
            series[i].setData(context.output(outputs[i]));
        }
    }

    applyPatch(context: IndicatorPainterPatchContext, series: any[]): boolean {
        const outputs: string[] = context.entry.outputNames?.length
            ? context.entry.outputNames
            : ['value'];
        return applyMappedRuntimePatch(context, series, outputs.map((output, seriesIndex) => ({
            outputId: output,
            seriesIndex,
            data: valuePoint,
        })));
    }
}
